import React from 'react';
import {
  Question, Serializer, SurveyModel,
} from 'survey-core';
import { ReactQuestionFactory, SurveyQuestionElementBase } from 'survey-react-ui';
import { PlaidConnect, SnapshotMode } from './plaid-connect';
import { patchSubmission } from '../../../api/submissions/patch-submission';
import {
  PlaidConnectionStatus, SurveySettings, Submission, UserType,
} from '../../../types';

const PLAID_CONNECT_QUESTION_TYPE = 'plaid-connect';
const DEFAULT_MAX_PANEL_COUNT = 5;

class PlaidConnectModel extends Question {
  getType () {
    return PLAID_CONNECT_QUESTION_TYPE;
  }

  get snapshotMode (): SnapshotMode {
    return this.getPropertyValue('snapshotMode', 'ONE_TIME');
  }

  set snapshotMode (value: SnapshotMode) {
    this.setPropertyValue('snapshotMode', value);
  }

  get maxPanelCount (): number {
    return this.getPropertyValue('maxPanelCount', DEFAULT_MAX_PANEL_COUNT);
  }

  set maxPanelCount (value: number) {
    this.setPropertyValue('maxPanelCount', value);
  }

  isEmpty (): boolean {
    const value = this.value;
    return !Array.isArray(value) || value.length === 0;
  }
}

Serializer.addClass(
  PLAID_CONNECT_QUESTION_TYPE,
  [],
  function () {
    return new PlaidConnectModel('');
  },
  'question',
);

Serializer.addProperty(PLAID_CONNECT_QUESTION_TYPE, {
  name: 'snapshotMode',
  choices: [ 'ONE_TIME', 'MONTHLY' ],
  default: 'ONE_TIME',
});

Serializer.addProperty(PLAID_CONNECT_QUESTION_TYPE, {
  name: 'maxPanelCount',
  type: 'number',
  default: DEFAULT_MAX_PANEL_COUNT,
});

interface PlaidConnectQuestionProps {
  question: PlaidConnectModel;
  settings?: SurveySettings;
  submission?: Submission;
  isSigner?: boolean;
}

class PlaidConnectQuestion extends SurveyQuestionElementBase {
  get typedProps (): PlaidConnectQuestionProps {
    return this.props as PlaidConnectQuestionProps;
  }

  get question (): PlaidConnectModel {
    return this.typedProps.question;
  }

  get userType (): UserType {
    return this.typedProps.isSigner ? UserType.SIGNER : UserType.INITIAL_USER;
  }

  handleConnectionsChanged = async (connections: PlaidConnectionStatus[]) => {
    this.question.value = connections;

    const survey = this.question.getSurvey() as SurveyModel;
    if (survey && this.typedProps.settings && this.typedProps.submission) {
      const data = {
        ...survey.data,
        [this.question.name]: connections,
      };
      await patchSubmission(this.typedProps.settings.urlSlug, this.typedProps.submission.publicId, data, this.userType);
    }
  };

  renderElement () {
    const {
      settings, submission,
    } = this.typedProps;

    return (
      <PlaidConnect
        currentUserType={this.userType}
        isReadOnly={this.question.isReadOnly || !submission || !settings}
        maxPanelCount={this.question.maxPanelCount}
        publicId={submission?.publicId ?? ''}
        questionName={this.question.name}
        snapshotMode={this.question.snapshotMode}
        urlSlug={settings?.urlSlug ?? ''}
        value={this.question.value}
        onConnectionsChanged={this.handleConnectionsChanged}
      />
    );
  }
}

interface RegisterPlaidConnectQuestionProps {
  settings?: SurveySettings;
  submission?: Submission;
  isSigner?: boolean;
}

export const registerPlaidConnectQuestion = ({
  settings,
  submission,
  isSigner,
}: RegisterPlaidConnectQuestionProps) => {
  ReactQuestionFactory.Instance.registerQuestion(
    PLAID_CONNECT_QUESTION_TYPE,
    (props) => {
      const incomingProps = props as unknown as Record<string, unknown> & { question: PlaidConnectModel };

      return React.createElement(PlaidConnectQuestion, {
        ...incomingProps,
        question: incomingProps.question,
        settings,
        submission,
        isSigner,
      });
    },
  );
};
